import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Group,
  Input,
  MultiSelect,
  MultiSelectProps,
  rem,
  Select,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  useDebouncedCallback,
  useDebouncedValue,
  useDisclosure,
} from "@mantine/hooks";
import {
  IconBarcode,
  IconBriefcase,
  IconBuildingWarehouse,
  IconFileExport,
  IconListDetails,
  IconPackage,
  IconSearch,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import {
  repositoryDelivery,
  repositoryPos,
  styleTableMantine,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { BASE_API_DELIVERY } from "../../../config";
import { _localization } from "../../../config/location";
import { green } from "../../../const/variables";
import { MessageResponse } from "../../../model/MessageResponse";
import { SelectListItemModel } from "../../../model/SelectListItem";
import { useLocation } from "react-router-dom";

interface SearchCriteria {
  keySearch?: string;
  branchCode?: string[]; // Mảng chứa các mã chi nhánh
  invCode?: string[]; // Mảng chứa các mã kho
  itemCode?: string[]; // Mảng chứa các mã sản phẩm
  itemKey?: string; // Mã sản phẩm hoặc khóa sản phẩm
  MaLoai?: string[]; // Mã loại sản phẩm
  MaChung?: string[]; // Mã chung sản phẩm
  MaNganh?: string[]; // Mã ngành sản phẩm
  MaLinhVuc?: string[];
}

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const InventoryReportList = () => {
  // Khởi tạo component InventoryReportList
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [visible, { toggle, close, open }] = useDisclosure(false);

  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [data, setData] = useState<InventoryItem[]>([]);
  const [dataInvCode, setDataInvCode] = useState<SelectListItemModel[]>([]);
  const [dataItemCode, setDataItemCode] = useState<SelectListItemModel[]>([]);
  const [dataCatSegment2b, setDataCatSegment2b] = useState<ComboboxItem[]>([]);
  const [dataCatSegment3c, setDataCatSegment3c] = useState<ComboboxItem[]>([]);
  const [dataCatSegment4d, setDataCatSegment4d] = useState<ComboboxItem[]>([]);
  const [rowCount, setRowCount] = useState(0);

  const [search, setSearch] = useState<SearchCriteria>({});
  const [search4d, setSearch4d] = useState<string>("");
  const [debounceSearch4d] = useDebouncedValue<string>(search4d, 500);

  const location = useLocation();

  const form = useForm<SearchCriteria>({
    mode: "uncontrolled",
    initialValues: {},
  });

  type InventoryItem = {
    idBranch: number;
    branchCode: string;
    branchName: string;
    idKho: number;
    maKho: string;
    tenKho: string;
    maSp: string;
    tenSp: string;
    dvt: string | null;
    countSoLuong: number;
    countSlChoXuat: number | null;
    countSlChoNhap: number | null;
    maLoai: string;
    tenLoai: string;
    maChung: string;
    tenChung: string;
    maNganh: string;
    tenNganh: string;
    maNhom: string;
    tenNhom: string;
    segment1Code: string | null;
    segment1Name: string | null;
    segment6Code: string | null;
    segment6Name: string | null;
    plvt1Code: string | null;
    plvt1Name: string | null;
    plvt3Code: string | null;
    plvt3Name: string | null;
    plvt4Code: string | null;
    plvt4Name: string | null;
    plvt5Code: string | null;
    plvt5Name: string | null;
    plvt6Code: string | null;
    plvt6Name: string | null;
  };

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: "STT",
        accessorKey: "STT",
        enableColumnActions: false,
        enableSorting: false,
        enableResizing: false,
        Cell: ({ row }) => <>{row?.index + 1}</>,
        size: 50,
      },
      {
        accessorKey: "maSp",
        header: "Mã sản phẩm",
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Badge
              radius="sm"
              variant="dot"
              size="md"
              style={{ cursor: "pointer" }}
              color={renderedCellValue === null ? "red" : green.base}
            >
              {renderedCellValue ?? "Không có mã sản phẩm"}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "branchCode",
        header: "Mã chi nhánh",
        size: 150,
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Badge variant="light" color="violet">
              {renderedCellValue}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "branchName",
        header: "Tên chi nhánh",
      },
      {
        accessorKey: "maKho",
        header: "Mã kho",
        size: 150,
        Cell: ({ renderedCellValue, cell, row }) => (
          <>
            <Badge variant="light" color="blue">
              {renderedCellValue}
            </Badge>
          </>
        ),
      },
      {
        accessorKey: "tenKho",
        header: "Tên kho",
        size: 225,
      },

      {
        accessorKey: "tenSp",
        header: "Tên sản phẩm",
        size: 300,
      },
      {
        accessorKey: "dvt",
        header: "Đơn vị tính",
        footer: "Tổng:",
        Footer(props) {
          return (
            <div>
              <Text c="red" fw={800}>
                {props.column.columnDef.footer}
              </Text>
            </div>
          );
        },
      },

      {
        accessorKey: "maNganh",
        header: "Mã ngành",
      },
      {
        accessorKey: "tenNganh",
        header: "Tên ngành",
        size: 200,
      },
      {
        accessorKey: "maLoai",
        header: "Mã loại",
      },
      {
        accessorKey: "tenLoai",
        header: "Tên loại",
      },
      {
        accessorKey: "maChung",
        header: "Mã chủng",
      },
      {
        accessorKey: "tenChung",
        header: "Tên chủng loại",
        size: 200,
      },
      {
        accessorKey: "maNhom",
        header: "Mã nhóm",
      },
      {
        accessorKey: "tenNhom",
        header: "Tên nhóm",
      },
      {
        accessorKey: "segment1Code",
        header: "Mã phân đoạn 1",
      },
      {
        accessorKey: "segment1Name",
        header: "Tên phân đoạn 1",
      },
      {
        accessorKey: "segment6Code",
        header: "Mã phân đoạn 6",
      },
      {
        accessorKey: "segment6Name",
        header: "Tên phân đoạn 6",
      },
      {
        accessorKey: "plvt1Code",
        header: "Mã plvt 1",
      },
      {
        accessorKey: "plvt1Name",
        header: "Tên plvt 1",
      },
      {
        accessorKey: "plvt3Code",
        header: "Mã plvt 3",
      },
      {
        accessorKey: "plvt3Name",
        header: "Tên plvt 3",
      },
      {
        accessorKey: "plvt4Code",
        header: "Mã plvt 4",
      },
      {
        accessorKey: "plvt4Name",
        header: "Tên plvt 4",
      },
      {
        accessorKey: "plvt5Code",
        header: "Mã plvt 5",
      },
      {
        accessorKey: "plvt5Name",
        header: "Tên plvt 5",
      },
      {
        accessorKey: "plvt6Code",
        header: "Mã plvt 6",
      },
      {
        accessorKey: "plvt6Name",
        header: "Tên plvt 6",
      },
      {
        accessorKey: "countSoLuong",
        header: "Tổng số lượng",
        Footer: ({ table }: any) => {
          const total = table
            .getRowModel()
            .rows.reduce(
              (sum: number, row: any) =>
                sum + (row.original?.countSoLuong || 0),
              0,
            );
          return (
            <div>
              <Text c="red" fw={800}>
                {total}
              </Text>
            </div>
          );
        },
      },
      {
        accessorKey: "countSlChoXuat",
        header: "Tổng số lượng chờ xuất",
        size: 200,
        Footer: ({ table }: any) => {
          const total = table
            .getRowModel()
            .rows.reduce(
              (sum: number, row: any) =>
                sum + (row.original?.countSlChoXuat || 0),
              0,
            );
          return (
            <div>
              <Text c="red" fw={800}>
                {total}
              </Text>
            </div>
          );
        },
      },
    ],
    [],
  );
  const exportDataExcel = async () => {
    open();
    try {
      let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;
      const search = form.getValues();
      if (search.branchCode && search.branchCode.length > 0) {
        search.branchCode.forEach((branchCode) => {
          url += `&BranchCode=${branchCode}`;
        });
      }
      if (search.invCode && search.invCode.length > 0) {
        search.invCode.forEach((invCode) => {
          url += `&InvId=${invCode}`;
        });
      }
      if (search.itemCode && search.itemCode.length > 0) {
        search.itemCode.forEach((itemCode) => {
          url += `&ItemCode=${itemCode}`;
        });
      }
      if (search.keySearch) {
        url += `&KeySearch=${search.keySearch.trim().toLowerCase()}`;
      }
      if (search.itemKey) {
        url += `&itemKey=${search.itemKey.trim().toLowerCase()}`;
      }
      if (search.MaLoai && search.MaLoai.length > 0) {
        search.MaLoai.forEach((maLoai) => {
          url += `&MaLoais=${maLoai.trim().toLowerCase()}`;
        });
      }
      if (search.MaChung && search.MaChung.length > 0) {
        search.MaChung.forEach((maChung) => {
          url += `&MaChungs=${maChung.trim().toLowerCase()}`;
        });
      }
      if (search.MaNganh && search.MaNganh.length > 0) {
        search.MaNganh.forEach((maNganh) => {
          url += `&MaNganhs=${maNganh.trim().toLowerCase()}`;
        });
      }
      if (search.MaLinhVuc && search.MaLinhVuc.length > 0) {
        search.MaLinhVuc.forEach((maLinhVuc) => {
          url += `&MaLinhVucs=${maLinhVuc.trim().toLowerCase()}`;
        });
      }
      // const baseUrl = `https://apis-pos-dev.hacom.vn/api-shipping/api/v1/ExportExcel/export-inventory-report${url}`;
      // // const baseUrl = `/api/v1/ExportExcel/export-inventory-report${url}`;
      // const fileName = "bao_cao_ton_kho.xlsx";

      // await exportDataToExcel(baseUrl, fileName);
      const baseUrl = `/api/v1/ExportExcel/export-inventory-report${url}`;

      const _url = BASE_API_DELIVERY + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });
      // Kiểm tra trạng thái HTTP
      if (!response.ok) {
        const errorResponse = await response.json(); // Parse lỗi từ API
        const errorMessage =
          errorResponse?.message || "Đã xảy ra lỗi khi xuất dữ liệu!";
        throw new Error(errorMessage); // Ném lỗi để xử lý trong `catch`
      }

      const blob = await response.blob();
      const urlss = window.URL.createObjectURL(blob);
      const _name = new Date().toISOString().slice(0, 10);
      let filename = "bao_cao_ton_kho.xlsx";
      const link = document.createElement("a");
      link.href = urlss;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.log("error", error);
      // Hiển thị thông báo lỗi
      NotificationExtension.Fails(
        error.message || "Đã xảy ra lỗi không xác định!",
      );
    } finally {
      close();
    }
  };
  // Hàm lấy dữ liệu
  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    let url = `?Skip=${pagination?.pageIndex * pagination?.pageSize}&Take=${
      pagination.pageSize
    }`;
    const search = form.getValues();

    if (search.branchCode && search.branchCode.length > 0) {
      search.branchCode.forEach((branchCode) => {
        url += `&BranchCode=${branchCode}`;
      });
    }
    if (search.invCode && search.invCode.length > 0) {
      search.invCode.forEach((invCode) => {
        url += `&InvId=${invCode}`;
      });
    }
    if (search.itemCode && search.itemCode.length > 0) {
      search.itemCode.forEach((itemCode) => {
        url += `&ItemCode=${itemCode}`;
      });
    }
    if (search.keySearch) {
      url += `&KeySearch=${search.keySearch.trim().toLowerCase()}`;
    }
    if (search.itemKey) {
      url += `&itemKey=${search.itemKey.trim().toLowerCase()}`;
    }
    if (search.MaLoai && search.MaLoai.length > 0) {
      search.MaLoai.forEach((maLoai) => {
        url += `&MaLoais=${maLoai.trim().toLowerCase()}`;
      });
    }
    if (search.MaChung && search.MaChung.length > 0) {
      search.MaChung.forEach((maChung) => {
        url += `&MaChungs=${maChung.trim().toLowerCase()}`;
      });
    }
    if (search.MaNganh && search.MaNganh.length > 0) {
      search.MaNganh.forEach((maNganh) => {
        url += `&MaNganhs=${maNganh.trim().toLowerCase()}`;
      });
    }
    if (search.MaLinhVuc && search.MaLinhVuc.length > 0) {
      search.MaLinhVuc.forEach((maLinhVuc) => {
        url += `&MaLinhVucs=${maLinhVuc.trim().toLowerCase()}`;
      });
    }
    try {
      const response = await repositoryDelivery.get(
        `/api/v1/TblInventorySerialOnhand/get-list${url}`,
      );
      if (response && response.success) {
        let result = response.data;
        setData(result);
        setRowCount(response.totalCount);
        table.resetRowSelection();
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const getCatSegment2b = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblCatSegment2b/get-all",
    );

    if (res && res.success) {
      let data: ComboboxItem[] = [];
      if (search?.MaLinhVuc && search?.MaLinhVuc?.length > 0) {
        data = res.data
          ?.filter(
            (item: any) =>
              item.codeString &&
              item.name &&
              item.segment1CodeString === search?.MaLinhVuc,
          )
          ?.map((item: any) => ({
            label: `${item.codeString} - ${item.name}`,
            value: item.codeString?.toString(),
          }));
      } else {
        data = res.data
          ?.filter((item: any) => item.codeString && item.name)
          ?.map((item: any) => ({
            label: `${item.codeString} - ${item.name}`,
            value: item.codeString?.toString(),
          }));
      }

      setDataCatSegment2b(data);
    }
  };

  const getCatSegment3c = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblCatSegment3c/get-all",
    );

    if (res && res.success) {
      let data: ComboboxItem[] = [];
      if (search?.MaNganh && search?.MaNganh.length > 0) {
        data = res.data
          ?.filter(
            (item: any) =>
              item.codeString &&
              item.name &&
              item.segment2CodeString === search?.MaNganh,
          )
          ?.map((item: any) => ({
            label: `${item.codeString} - ${item.name}`,
            value: item.codeString?.toString(),
          }));
      } else {
        data = res.data
          ?.filter((item: any) => item.codeString && item.name)
          ?.map((item: any) => ({
            label: `${item.codeString} - ${item.name}`,
            value: item.codeString?.toString(),
          }));
      }

      setDataCatSegment3c(data);
    }
  };

  const getCatSegment4d = async () => {
    let url = `/api/v1/TblCatSegment4d/get-list?Skip=0&Take=50`;

    if (search?.MaLoai && search?.MaLoai?.length > 0) {
      url += `&listCodeSegmentString3=${search?.MaLoai}`;
    }

    if (debounceSearch4d.length > 3 && search?.MaChung?.length === 0) {
      url += `&KeySearch=${debounceSearch4d}`;
    } else {
      url += `&KeySearch=${debounceSearch4d?.split(" - ")[0]}`;
    }

    const res = await repositoryPos.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const data = res.data
        ?.filter((item: any) => item.codeString && item.name)
        ?.map((item: any) => ({
          label: `${item.codeString} - ${item.name}`,
          value: item.codeString?.toString(),
        }));

      setDataCatSegment4d(data);
    }
  };

  const DataInvCode = async () => {
    // const user = JSON.parse(localStorage.getItem("userLogin") || "{}");
    // const Invcode = user?.branchId;
    try {
      const response = await repositoryPos.get(
        `/api/v1/TblDmInventory/get-all?checkAuth=true&select=true`,
      );
      if (response && response.success) {
        const transformedData = response.data
          ?.filter((i: any) => i.value && i.text && i.value !== "-1")
          ?.map((item: any) => ({
            label: item.text?.toString(),
            value: String(item.att2),
          }));

        setDataInvCode(transformedData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };
  const DataItemCode = async (keySearch: string) => {
    try {
      let url = "";
      if (keySearch && keySearch.length > 0) {
        url += `&ItemCodes=${keySearch}`;
      }
      // const response = await repositoryDelivery.get(
      //   `api/v1/TblInventorySerialOnhand/get-list-item-by-inv?Skip=0&Take=50` +
      //     url
      // );
      const response = await repositoryPos.get(
        `/api/v1/TblItem/get-select?Key=${keySearch}&Page=30`,
      );
      if (response && response.success) {
        // const transformedData = response.data
        //   ?.filter((i: any) => i.maSp && i.tenSp)
        //   .map((branch: any) => ({
        //     value: branch.maSp,
        //     label: branch.maSp + " - " + branch.tenSp,
        //   }));
        const transformedData = response.data
          ?.filter((i: any) => i.value && i.text)
          .map((branch: any) => ({
            value: branch.value,
            label: branch.text,
          }));
        const uniqueData = Array.from(
          new Set(transformedData.map((item: any) => item.value)),
        ).map((value) =>
          transformedData.find((item: any) => item.value === value),
        );
        setDataItemCode(uniqueData);
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningInventoryReportList",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["STT", "maSp", "transactionNumber", "sourceCode"],
          right: [
            "statusName",
            "countSoLuong",
            "countSlChoXuat",
            "countSlChoNhap",
          ],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningInventoryReportList",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: true,
    rowCount,
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: true,
    // manualPagination: true,
    enableStickyFooter: true,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
      columnPinning,
    },
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    onPaginationChange: setPagination,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    positionToolbarAlertBanner: "bottom",
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        ...columnPinning,
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    getRowId: (row) => row.departmentId,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    mantineTableBodyCellProps: ({ row }) => ({
      style: styleTableMantine,
    }),
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,

    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
  });
  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (165 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);
  useEffect(() => {
    fetchData();
  }, [pagination]);

  useEffect(() => {
    getCatSegment2b();

    // if (!search?.MaLinhVuc || search?.MaLinhVuc?.length === 0) {
    //   setSearch((prev) => ({
    //     ...prev,
    //     MaNganh: "",
    //     MaLoai: "",
    //     MaChung: "",
    //     MaNhom: "",
    //     MaModel: "",
    //   }));
    // }
  }, [search.MaLinhVuc]);

  useEffect(() => {
    getCatSegment3c();

    // if (!search?.MaNganh || search?.MaNganh?.length === 0) {
    //   setSearch((prev) => ({
    //     ...prev,
    //     MaLoai: "",
    //     MaChung: "",
    //     MaNhom: "",
    //     MaModel: "",
    //   }));
    // }
  }, [search.MaNganh]);

  useEffect(() => {
    getCatSegment4d();

    // if (!search?.MaLoai || search?.MaLoai?.length === 0) {
    //   setSearch((prev) => ({
    //     ...prev,
    //     MaChung: "",
    //     MaNhom: "",
    //     MaModel: "",
    //   }));
    // }
  }, [search.MaLoai, debounceSearch4d]);

  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => (
    <Group gap="sm">
      <Text>{dataItemCode.find((x) => x.value === option.value)?.label}</Text>
    </Group>
  );
  // Sử dụng debounce để trì hoãn việc gọi API
  const debouncedDataItemCode = useDebouncedCallback((value: string) => {
    if (value.length >= 3) {
      DataItemCode(value); // Gọi API với từ khóa tìm kiếm
    }
  }, 500);
  return (
    <Box>
      <LoadingOverlayCustom visible={visible} />
      <div ref={headerRef} style={{ width: "100%", marginBottom: "10px" }}>
        <Flex justify={"space-between"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Button
            leftSection={
              <IconFileExport style={{ width: rem(14), height: rem(14) }} />
            }
            onClick={() => exportDataExcel()}
          >
            Export Excel
          </Button>
        </Flex>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Input
              placeholder="Nhập tìm kiếm"
              key={form.key("keySearch")}
              type="text"
              leftSection={<IconSearch size={"20"} color="#15aabf" />}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await fetchData();
                }
              }}
              {...form.getInputProps("keySearch")}
            />
          </Grid.Col>
          {/* <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Input
              placeholder="Mã sản phẩm"
              key={form.key("itemKey")}
              type="text"
              leftSection={<IconSearch size={"20"} color="#15aabf" />}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await fetchData();
                }
              }}
              {...form.getInputProps("itemKey")}
            />
          </Grid.Col> */}
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <MultiSelect
              key={form.key("invCode")}
              size="sm"
              placeholder="Mã kho"
              searchable
              clearable
              data={dataInvCode}
              leftSection={<IconBuildingWarehouse size={20} color="#15aabf" />}
              onClick={() => {
                if (dataInvCode.length === 0) {
                  DataInvCode();
                }
              }}
              {...form.getInputProps("invCode")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <MultiSelect
              key={form.key("itemCode")}
              size="sm"
              placeholder="Mã sản phẩm"
              clearable
              searchable
              data={dataItemCode.map((x) => x.value)} // Hiển thị kết quả từ API
              //   value={search.itemCode}
              renderOption={renderMultiSelectOption}
              leftSection={<IconBarcode size={20} color="#15aabf" />}
              // onSearchChange={(value) => {
              //   if (value.length >= 3) {
              //     DataItemCode(value); // Gọi API với từ khóa tìm kiếm
              //   }
              // }}
              onSearchChange={(value) => {
                debouncedDataItemCode(value); // Sử dụng debounce
              }}
              // onChange={(value) => {
              //   handleChangeSearchValue(value, "itemCode");
              // }}
              {...form.getInputProps("itemCode")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Mã ngành"
              data={dataCatSegment2b}
              leftSection={<IconBriefcase color="#15aabf" />}
              searchable
              clearable
              value={
                search?.MaNganh && search?.MaNganh?.length > 0
                  ? search?.MaNganh?.toString()
                  : null
              }
              onChange={(e) => {
                changeSearchValue(e ? e : "", "MaNganh", setSearch);
                // setSearch((prev) => ({
                //   ...prev,
                //   MaLoai: "",
                //   MaChung: "",
                //   MaNhom: "",
                //   MaModel: "",
                // }));
              }}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Mã loại"
              data={dataCatSegment3c}
              leftSection={<IconPackage color="#15aabf" />}
              searchable
              clearable
              value={
                search?.MaLoai && search?.MaLoai?.length > 0
                  ? search?.MaLoai?.toString()
                  : null
              }
              onChange={(e) => {
                changeSearchValue(e ? e : "", "MaLoai", setSearch);
                // setSearch((prev) => ({
                //   ...prev,
                //   MaChung: "",
                //   MaNhom: "",
                //   MaModel: "",
                // }));
              }}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Mã chủng"
              data={dataCatSegment4d}
              leftSection={<IconListDetails color="#15aabf" />}
              searchable
              searchValue={search4d}
              onSearchChange={setSearch4d}
              clearable
              value={
                search?.MaChung && search?.MaChung?.length > 0
                  ? search?.MaChung?.toString()
                  : null
              }
              onChange={(e) => {
                changeSearchValue(e ? e : "", "MaChung", setSearch);
                // setSearch((prev) => ({
                //   ...prev,
                //   MaNhom: "",
                //   MaModel: "",
                // }));
              }}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Button
              color="blue"
              variant="outline"
              onClick={async () => {
                await fetchData();
              }}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <MantineReactTable table={table} />
    </Box>
  );
};

export default InventoryReportList;
